<script lang="ts" setup>
import { useTitle } from '@vueuse/core'
import { useExporter } from '~/modules/exporter'
import { usePredictionParams } from '~/modules/predictionParams'
import { useMatterInfo } from '~/modules/matterInfo'
import { usePercentiles } from '~/modules/percentiles'
import { dateFormat } from '~/utils'

import Header from '~/components/Header.vue'
import PredictionIntro from '~/components/PredictionIntro.vue'
import AppLoading from '~/components/AppLoading.vue'

const PredictionReport = defineAsyncComponent(() => import('~/components/PredictionReport.vue'))

const showPredictionDetails = ref(false)

const { matterInfo } = useMatterInfo()

const { predictionParameters } = usePredictionParams()

const { isFetching: isPredictionDataFetching, predictionData, currentPercentiles, damages, isAiPrediction, recommendation, settlementDays, thirdPartyCosts, runPrediction, resetPrediction } = usePercentiles()

const today = computed(() => dateFormat.format(new Date()))

const { isPdfModalOpen, isCommentEditorOpen, reportComment, cancelPdf, addComment, createPdf } = useExporter()

const title = computed(() => {
  if (predictionData.value && matterInfo.value)
    return `PREDiCT LL ${matterInfo.value.weightmansReference} ${today.value}`

  return 'PREDiCT Large Loss'
})

useTitle(title)
</script>

<template>
  <div class="main">
    <Header v-bind="matterInfo" :has-prediction="!!predictionData" @click:pdf-report="isPdfModalOpen = true" />
    <template v-if="!!predictionData || isPredictionDataFetching">
      <Suspense>
        <PredictionReport
          v-model:show-prediction-details="showPredictionDetails"
          v-model:report-comment="reportComment"
          v-model:current-percentiles="currentPercentiles"
          v-model:is-pdf-modal-open="isPdfModalOpen"
          :damages="damages"
          :is-ai-damages="isAiPrediction.damagesPredictions"
          :claimant-costs="thirdPartyCosts"
          :is-ai-claimant-costs="isAiPrediction.thirdPartyCostsPredictions"
          :settlement-days="settlementDays"
          :is-ai-settlement-days="isAiPrediction.settlementTimePredictions"
          :recommendation="recommendation"
          :details="predictionParameters"
          :report-date="today"
          :is-comment-editor-open="isCommentEditorOpen"
          :prediction-data="predictionData"
          @click:reset-prediction="resetPrediction($event)"
          @click:add-comment="addComment"
          @click:create-pdf="createPdf"
          @click:cancel-pdf="cancelPdf"
        />
        <template #fallback>
          <AppLoading />
        </template>
      </Suspense>
    </template>
    <template v-else>
      <PredictionIntro :has-matter-info="!!matterInfo" @click:run-predict="runPrediction()" />
    </template>
  </div>
</template>

<style lang="scss" scoped>
.main {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
}
</style>

<route lang="yaml">
name: index
meta:
  layout: authenticated
  requiresAuth: true
</route>
